/**
 * triggerTools.ts — pure helpers for ⚡ trigger management (the trigger
 * contract's UI half).
 *
 * Everything here is client-side and pure: the condition builder that turns
 * {signal, operator, value} into the CEL expr the relay path evaluates, the
 * reverse parse (expr → builder fields, best-effort), and the DRY-RUN —
 * evaluating a trigger's expr over captured ticks with the SAME walker +
 * semantics the backend's `_apply_workflow_triggers` uses (truthiness > 0.5,
 * rising edge via the cooldown latch), so "would this ever fire?" is answered
 * before the backend ever runs it.
 */
import type { TickPayload } from '../engine/types';
import { tryEvalExpr } from '../engine/expr/walker';

/** The expr scope over one tick — mirrors backend `_trigger_scope` exactly:
 * `nodes.<id>.stock/.inflow/.outflow/.state.<k>`, `edges.<id>.flow`,
 * `metrics.<k>`, `params.<path>`, `step`. */
export function triggerScopeOf(tick: TickPayload): Record<string, unknown> {
  const nodes: Record<string, Record<string, number>> = {};
  for (const [nid, v] of Object.entries(tick.nodeValues ?? {})) {
    const entry: Record<string, number> = {
      stock: v.stock, inflow: v.inflow, outflow: v.outflow,
    };
    if (v.state) Object.assign(entry, v.state);
    nodes[nid] = entry;
  }
  const edges: Record<string, { flow: number }> = {};
  for (const [eid, f] of Object.entries(tick.edgeFlows ?? {})) {
    edges[eid] = { flow: f };
  }
  return {
    step: tick.step,
    nodes,
    edges,
    metrics: { ...(tick.metricValues ?? {}) },
    params: { ...(tick.params ?? {}) },
  };
}

export interface DryRunResult {
  /** Steps where the condition held (truthy), in order. */
  holds: number[];
  /** Steps where the trigger WOULD have fired (held + cooldown elapsed). */
  fires: number[];
  /** First evaluation error (bad expr), if any. */
  error: string | null;
  /** Per-step trace of the LHS signal value when a single-signal expr is
   * detected — drives the mini value display. null entries = unresolvable. */
  values: (number | null)[];
  /** Whether the condition holds on the most recent tick. */
  current: boolean;
}

/** Simulate a trigger over captured ticks with the backend's exact firing
 * semantics: an expr is held when it evaluates > 0.5; a held expr fires when
 * `step - lastFired >= cooldown` (the rising-edge + cooldown latch). */
export function dryRunTrigger(
  expr: string,
  ticks: TickPayload[],
  cooldown: number,
): DryRunResult {
  const holds: number[] = [];
  const fires: number[] = [];
  const values: (number | null)[] = [];
  let error: string | null = null;
  let lastFired = Number.NEGATIVE_INFINITY;
  for (const t of ticks) {
    const scope = triggerScopeOf(t);
    const res = tryEvalExpr(expr, scope);
    if (res.error) {
      error = res.error;
      values.push(null);
      break;
    }
    values.push(res.value);
    const held = (res.value ?? 0) > 0.5;
    if (held) holds.push(t.step);
    if (held && t.step - lastFired >= cooldown) {
      fires.push(t.step);
      lastFired = t.step;
    }
  }
  const last = ticks[ticks.length - 1];
  const current = last ? holds.includes(last.step) : false;
  return { holds, fires, error, values, current };
}

// ─── The condition builder ──────────────────────────────────────────────────

export const TRIGGER_OPERATORS = ['>', '>=', '<', '<=', '==', '!='] as const;
export type TriggerOperator = (typeof TRIGGER_OPERATORS)[number];

export interface SignalOption {
  /** The full CEL path, e.g. `nodes.anomaly.stock` or `metrics.pnl`. */
  path: string;
  /** Human label for the dropdown. */
  label: string;
}

/** Every signal the expr vocabulary can address, derived from the bundle +
 * the latest tick (state keys are only knowable from observed values). */
export function signalOptions(
  bundle: { graph: { nodes: { id: string; label?: string }[]; edges: { id: string }[] } },
  metrics: string[],
  params: string[],
  lastTick: TickPayload | null,
): SignalOption[] {
  const out: SignalOption[] = [{ path: 'step', label: 'step (tick number)' }];
  for (const n of bundle.graph.nodes) {
    const name = n.label ? `${n.label} (${n.id})` : n.id;
    out.push(
      { path: `nodes.${n.id}.stock`, label: `${name} · stock` },
      { path: `nodes.${n.id}.inflow`, label: `${name} · inflow` },
      { path: `nodes.${n.id}.outflow`, label: `${name} · outflow` },
    );
    const stateKeys = Object.keys(lastTick?.nodeValues?.[n.id]?.state ?? {});
    for (const k of stateKeys) {
      out.push({ path: `nodes.${n.id}.state.${k}`, label: `${name} · state.${k}` });
    }
  }
  for (const e of bundle.graph.edges) {
    out.push({ path: `edges.${e.id}.flow`, label: `edge ${e.id} · flow` });
  }
  for (const m of metrics) out.push({ path: `metrics.${m}`, label: `metric ${m}` });
  for (const p of params) out.push({ path: `params.${p}`, label: `param ${p}` });
  return out;
}

/** Builder fields → CEL expr: `<signal> <op> <value>`. A raw expr that isn't
 * a simple comparison round-trips through `custom`. */
export function buildConditionExpr(
  signal: string, op: TriggerOperator, value: string,
): string {
  return `${signal} ${op} ${value.trim()}`;
}

export interface ParsedCondition {
  signal: string;
  op: TriggerOperator;
  value: string;
  /** false when the expr is not a simple `<path> <op> <literal>` comparison —
   *  the builder then shows it as free text (advanced mode). */
  simple: boolean;
}

/** Best-effort expr → builder fields. Understands exactly the shape
 *  `buildConditionExpr` produces — including compound left-hand sides like
 *  `step % 600 == 0` (the schedule pattern round-trips through the builder).
 *  Anything else (multi-comparison, arithmetic on the right) falls back to
 *  free text (advanced mode). */
export function parseConditionExpr(expr: string): ParsedCondition | null {
  const m = /^(.+?)\s*(>=|<=|==|!=|>|<)\s*(-?\d+(?:\.\d+)?)$/.exec(expr.trim());
  if (!m) return null;
  return { signal: m[1].trim(), op: m[2] as TriggerOperator, value: m[3], simple: true };
}
